import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './Admin.css';

// Fetch calls in this code:

// 1. POST /api/classes
// Purpose: Create a new class
// Destination: backend/class_routes.py - @class_bp.route('', methods=['POST'])

// 2. PUT /api/classes/<int:class_id>
// Purpose: Update an existing class
// Destination: backend/class_routes.py - @class_bp.route('/<int:class_id>', methods=['PUT'])

// 3. POST /api/classes/<int:class_id>/book
// Purpose: Book a class
// Destination: backend/booking_routes.py - @booking_bp.route('/create', methods=['POST'])

// 4. POST /api/classes/<int:class_id>/cancel
// Purpose: Cancel a class booking
// Destination: backend/booking_routes.py - @booking_bp.route('/<int:booking_id>/cancel', methods=['POST'])

// 5. DELETE /api/admin/bookings
// Purpose: Remove a booking (admin functionality)
// Destination: backend/adminscheduleroutes.py - @admin_schedule_bp.route('/api/admin/bookings', methods=['DELETE'])

// 6. GET /api/classes/<int:class_id>/bookings
// Purpose: Get bookings for a specific class
// Destination: backend/class_routes.py - @class_bp.route('/<int:class_id>/bookings', methods=['GET'])

// 7. GET /api/classes/<int:class_id>/waitlist
// Purpose: Get waitlist for a class
// Destination: backend/class_routes.py - @class_bp.route('/<int:class_id>/waitlist', methods=['GET'])

// 8. POST /api/classes/<int:class_id>/waitlist
// Purpose: Add user to class waitlist
// Destination: backend/class_routes.py - @class_bp.route('/<int:class_id>/waitlist', methods=['POST'])

// 9. DELETE /api/classes/<int:class_id>/waitlist
// Purpose: Remove user from class waitlist
// Destination: backend/class_routes.py - @class_bp.route('/<int:class_id>/waitlist', methods=['DELETE'])

// 10. GET /api/classes/<int:class_id>/attendance
// Purpose: Get class attendance
// Destination: backend/class_routes.py - @class_bp.route('/<int:class_id>/attendance', methods=['GET'])

// 11. POST /api/classes/<int:class_id>/attendance
// Purpose: Mark class attendance
// Destination: backend/class_routes.py - @class_bp.route('/<int:class_id>/attendance', methods=['POST'])

// 12. PUT /api/classes/<int:class_id>/attendance
// Purpose: Update class attendance
// Destination: backend/class_routes.py - @class_bp.route('/<int:class_id>/attendance', methods=['PUT'])

// Define initial class data
const initialClassData = {
  name: '',
  instructor_id: '',
  description: '',
  location: '',
  start_time: '09:00',
  end_time: '10:00',
  max_capacity: 15,
  price: 10,
  difficulty: 1,
  workshop: 'no',
  repeat_weekly: false,
  repeat_count: 2,
  prepayment: ''
};

/**
 * ClassForm component for adding/editing yoga classes
 */
const ClassForm = ({ instructors, initialData, selectedDate, onSubmit, onCancel }) => {
  const { t, i18n } = useTranslation();
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    instructor_id: '',
    start_time: '08:00',
    end_time: '09:00',
    max_capacity: 15,
    location: 'Main Studio',
    name: '',
    description: '',
    difficulty: 1,
    repeat_weekly: false,
    repeat_count: 4,
    price: 0,
    workshop: 'no',
    prepayment: ''
  });
  const [selectedImages, setSelectedImages] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [benefits, setBenefits] = useState(['']);
  
  // Force re-render on language change
  useEffect(() => {
    // This empty effect depends on i18n.language
    // and will trigger a re-render when language changes
  }, [i18n.language]);
  
  // Initialize form with selected class data if editing
  useEffect(() => {
    if (initialData) {
      const startTime = initialData.start_time;
      const endTime = initialData.end_time;
      
      // Determine if this is a workshop by checking various possible properties
      let isWorkshop = 'no';
      if (initialData.workshop === 'yes' || 
          initialData.workshop === true || 
          initialData.is_workshop === true ||
          initialData.type === 'workshop') {
        isWorkshop = 'yes';
      }
      
          setFormData({
        instructor_id: initialData.instructor_id || '',
        start_time: startTime,
        end_time: endTime,
        max_capacity: initialData.max_capacity || 15,
        location: initialData.location || 'Main Studio',
        name: initialData.title || '',
        description: initialData.description || '',
        difficulty: initialData.difficulty || 1,
        repeat_weekly: false, // Cannot edit recurring classes
        repeat_count: 4,
        price: initialData.price || 0,
        workshop: isWorkshop,
        prepayment: initialData.prepayment || ''
      });
      
      // If there's an image_url, set it in the preview
      if (initialData.image_url) {
        setPreviewUrls(Array.isArray(initialData.image_url) 
          ? initialData.image_url 
          : [initialData.image_url]);
      }

      // Initialize benefits if available
      if (initialData.benefits) {
        try {
          const parsedBenefits = JSON.parse(initialData.benefits);
          setBenefits(Array.isArray(parsedBenefits) ? parsedBenefits : [initialData.benefits]);
        } catch (e) {
          // If not valid JSON, treat as single benefit
          setBenefits([initialData.benefits]);
        }
      }
    } else {
      // Reset form for new class
      setFormData({
        instructor_id: instructors.length > 0 ? instructors[0].id : '',
        start_time: '08:00',
        end_time: '09:00',
        max_capacity: 15,
        location: 'Main Studio',
        name: '',
        description: '',
        difficulty: 1,
        repeat_weekly: false,
        repeat_count: 4,
        price: 0,
        workshop: 'no',
        prepayment: ''
      });
      setSelectedImages([]);
      setPreviewUrls([]);
      setBenefits(['']);
    }
  }, [initialData, instructors]);
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // For checkbox inputs, use the checked property
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData({
      ...formData,
      [name]: newValue
    });
  };
  
  // Handle image selection
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length > 0) {
      // For edit mode, completely replace the selected images when new ones are chosen
      if (initialData) {
        setSelectedImages(files);
        
        // Clear previous preview URLs to avoid memory leaks
        previewUrls.forEach(url => URL.revokeObjectURL(url));
        
        // Create preview URLs for the selected images
        const newPreviewUrls = files.map(file => URL.createObjectURL(file));
        setPreviewUrls(newPreviewUrls);
      } else {
        // For create mode, append to existing selected images
        setSelectedImages(prev => [...prev, ...files]);
        
        // Create preview URLs for the selected images
        const newPreviewUrls = files.map(file => URL.createObjectURL(file));
        setPreviewUrls(prev => [...prev, ...newPreviewUrls]);
      }
    }
  };
  
  // Remove a selected image
  const removeImage = (index) => {
    const updatedImages = [...selectedImages];
    const updatedPreviews = [...previewUrls];
    
    // Revoke the object URL to avoid memory leaks
    URL.revokeObjectURL(updatedPreviews[index]);
    
    updatedImages.splice(index, 1);
    updatedPreviews.splice(index, 1);
    
    setSelectedImages(updatedImages);
    setPreviewUrls(updatedPreviews);
  };
  
  // Handle benefit changes
  const handleBenefitChange = (index, value) => {
    const updatedBenefits = [...benefits];
    updatedBenefits[index] = value;
    setBenefits(updatedBenefits);
  };
  
  // Add a new benefit field
  const addBenefitField = () => {
    if (benefits.length < 5) {
      setBenefits([...benefits, '']);
    }
  };
  
  // Remove a benefit field
  const removeBenefitField = (index) => {
    const updatedBenefits = [...benefits];
    updatedBenefits.splice(index, 1);
    setBenefits(updatedBenefits);
  };
  
  // Trigger file input click
  const openFileSelector = () => {
    fileInputRef.current.click();
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Calculate the date and time for API
    const dateObj = new Date(selectedDate);
    const [startHours, startMinutes] = formData.start_time.split(':');
    const [endHours, endMinutes] = formData.end_time.split(':');
    
    const startDate = new Date(dateObj);
    startDate.setHours(parseInt(startHours), parseInt(startMinutes), 0, 0);
    
    const endDate = new Date(dateObj);
    endDate.setHours(parseInt(endHours), parseInt(endMinutes), 0, 0);
    
    // Preserve local time by adjusting for timezone offset
    const startISOString = (() => {
      const tzOffset = startDate.getTimezoneOffset();
      const adjustedDate = new Date(startDate);
      adjustedDate.setMinutes(adjustedDate.getMinutes() - tzOffset);
      return adjustedDate.toISOString();
    })();
    
    const endISOString = (() => {
      const tzOffset = endDate.getTimezoneOffset();
      const adjustedDate = new Date(endDate);
      adjustedDate.setMinutes(adjustedDate.getMinutes() - tzOffset);
      return adjustedDate.toISOString();
    })();
    
    // Filter out empty benefits
    const filteredBenefits = benefits.filter(benefit => benefit.trim() !== '');
    
    // Prepare data for API
    const apiData = {
      ...formData,
      start_time: startISOString,
      end_time: endISOString,
      instructor_id: parseInt(formData.instructor_id),
      max_capacity: parseInt(formData.max_capacity),
      price: parseFloat(formData.price || 0),
      difficulty: parseInt(formData.difficulty || 1),
      // Set repeat_count to 1 if repeat_weekly is false
      repeat_count: formData.repeat_weekly ? parseInt(formData.repeat_count) : 1,
      images: selectedImages,
      benefits: filteredBenefits.length > 0 ? JSON.stringify(filteredBenefits) : null,
      // Only replace images when editing a class AND new images are selected
      replace_images: initialData && selectedImages.length > 0,
      // Handle prepayment - only include if it's a workshop and prepayment text is provided
      prepayment: formData.workshop === 'yes' && formData.prepayment ? formData.prepayment : null
    };
    
    onSubmit(apiData);
  };
  
  // Clean up preview URLs when component unmounts
  useEffect(() => {
    return () => {
      previewUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);
  
  return (
    <div className="class-form">
      <h3>{initialData ? t('admin.editClass') : t('admin.addNewClass')}</h3>
      
      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">{t('admin.className')}</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={t('admin.className')}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="instructor_id">{t('admin.instructor')}</label>
            <select 
              id="instructor_id" 
              name="instructor_id"
              value={formData.instructor_id}
              onChange={handleChange}
              required
            >
              <option value="">{t('admin.selectInstructor')}</option>
              {instructors.map(instructor => (
                <option key={instructor.id} value={instructor.id}>
                  {instructor.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="location">{t('admin.location')}</label>
            <input
              type="text"
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder={t('admin.location')}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="difficulty">{t('admin.difficultyLevel')}</label>
            <input
              type="number"
              id="difficulty"
              name="difficulty"
              value={formData.difficulty}
              onChange={handleChange}
              min="1"
              max="3"
              required
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="start_time">{t('admin.startTime')}</label>
            <input
              type="time"
              id="start_time"
              name="start_time"
              value={formData.start_time}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="end_time">{t('admin.endTime')}</label>
            <input
              type="time"
              id="end_time"
              name="end_time"
              value={formData.end_time}
              onChange={handleChange}
              required
            />
          </div>
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="max_capacity">{t('admin.maximumCapacity')}</label>
            <input
              type="number"
              id="max_capacity"
              name="max_capacity"
              value={formData.max_capacity}
              onChange={handleChange}
              min="1"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="price">{t('admin.price')} (€)</label>
            <input
              type="number"
              id="price"
              name="price"
              value={formData.price}
              onChange={handleChange}
              min="0"
              step="1"
              required
            />
          </div>
          
          <div className="form-group">
            <label>{t('admin.classType')}</label>
            <div className="toggle-switch-container">
              <div className="toggle-switch">
                <input
                  type="radio"
                  id="regular-class"
                  name="workshop"
                  value="no"
                  checked={formData.workshop === 'no'}
                  onChange={handleChange}
                />
                <input
                  type="radio"
                  id="workshop-class"
                  name="workshop"
                  value="yes"
                  checked={formData.workshop === 'yes'}
                  onChange={handleChange}
                />
                <div className="toggle-switch-labels">
                  <label htmlFor="regular-class" className="toggle-label-option">{t('admin.regular')}</label>
                  <label htmlFor="workshop-class" className="toggle-label-option">{t('admin.workshop')}</label>
                </div>
                <div className="toggle-slider"></div>
              </div>
            </div>
          </div>
          
          {/* Prepayment options - only shown for workshops */}
          {formData.workshop === 'yes' && (
            <div className="form-group prepayment-section">
              <div className="prepayment-instructions">
                <label htmlFor="prepayment">{t('admin.prepaymentInstructions') || 'Prepayment instructions'}</label>
                <p className="prepayment-help-text">{t('admin.prepaymentHelpText') || 'Enter payment instructions for this workshop. Leave empty if no prepayment is required.'}</p>
                <textarea
                  id="prepayment"
                  name="prepayment"
                  value={formData.prepayment}
                  onChange={handleChange}
                  rows="3"
                  placeholder={t('admin.prepaymentPlaceholder') || 'Pre tento workshop je nutná platba vopred na číslo...'}
                />
              </div>
            </div>
          )}
          
          {!initialData && formData.workshop === 'no' && (
            <div className="form-group">
              <div className="repeat-options">
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="repeat_weekly"
                    name="repeat_weekly"
                    checked={formData.repeat_weekly}
                    onChange={handleChange}
                  />
                  <label htmlFor="repeat_weekly">{t('admin.repeatWeekly')}</label>
                </div>
                
                {formData.repeat_weekly && (
                  <div className="repeat-count">
                    <label htmlFor="repeat_count">{t('admin.repeatFor')}</label>
                    <select
                      id="repeat_count"
                      name="repeat_count"
                      value={formData.repeat_count}
                      onChange={handleChange}
                    >
                      {[2, 3, 4, 5, 6, 7, 8].map(num => (
                        <option key={num} value={num}>
                          {num} {t('admin.weeks')}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        <div className="form-group full-width">
          <label htmlFor="description">{t('admin.description')}</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows="5"
            placeholder={t('admin.descriptionPlaceholder')}
            style={{ whiteSpace: 'pre-wrap' }}
          />
          {/* <small className="form-text">
            Enter your text with line breaks and spacing as you want it to appear. The text formatting will be preserved.
          </small> */}
        </div>
        
        {/* Benefits Section */}
        <div className="form-group full-width">
          <label>{t('admin.benefits')} ({t('admin.max')} 5)</label>
          <div className="benefits-container">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-row">
                <input
                  type="text"
                  placeholder={t('admin.enterBenefit')}
                  value={benefit}
                  onChange={(e) => handleBenefitChange(index, e.target.value)}
                  className="benefit-input"
                />
                <button
                  type="button"
                  className="remove-benefit-btn"
                  onClick={() => removeBenefitField(index)}
                >
                  ×
                </button>
              </div>
            ))}
            {benefits.length < 5 && (
              <button
                type="button"
                className="add-benefit-btn"
                onClick={addBenefitField}
              >
                + {t('admin.addBenefit')}
              </button>
            )}
          </div>
        </div>
        
        <div className="form-group full-width image-upload-section">
          <label>{t('admin.classImages')}</label>
          <div className="upload-container">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageChange}
              accept=".jpg, image/jpeg"
              multiple
              style={{ display: 'none' }}
            />
            <button 
              type="button" 
              className="upload-button" 
              onClick={openFileSelector}
            >
              {t('admin.selectImages')}
            </button>
            <span className="upload-hint">
              {t('admin.imageFormatHint')} (.jpg only, max velkost kazdej photo 3MB)
            </span>
            {initialData && (
              <div className="replace-notice">
                {t('admin.imageReplaceNotice')}
              </div>
            )}
          </div>
          
          {previewUrls.length > 0 && (
            <div className="image-previews">
              {previewUrls.map((url, index) => (
                <div key={index} className="image-preview-item">
                  <img src={url} alt={`${t('admin.preview')} ${index + 1}`} />
                  <button 
                    type="button" 
                    className="remove-image" 
                    onClick={() => removeImage(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="form-actions">
          <button type="button" className="cancel-button" onClick={onCancel}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="save-button">
            {initialData ? t('admin.updateClass') : t('admin.createClass')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ClassForm; 